import { motion, AnimatePresence, useInView, useAnimate } from 'framer-motion';
import { StackedContent, MotionImage, useSanitizedTranslation, TextBlock } from 'ui';
import { useState, useRef, useEffect } from 'react';
import { TimelineCarousel, TimelineCarouselItem } from './EventsCarousel';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';

export interface TimelineProps {
  content: any;
  carouselItems: TimelineCarouselItem[];
}

const Timeline = ({ content, carouselItems }: TimelineProps) => {
  const { t } = useSanitizedTranslation();

  const [ref, animate] = useAnimate();
  const inView = useInView(ref, { once: true, margin: '-30% 0% -30% 0%' });
  const carouselScrollRef = useRef<HTMLDivElement | null>(null);

  // keep buttons visible; only disable when at edges
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateArrows = () => {
    const el = carouselScrollRef.current;
    if (!el) return;
    const maxScroll = Math.max(el.scrollWidth - el.clientWidth, 0);
    const current = el.scrollLeft;
    const EPS = 2; // tiny buffer for fractional scroll values

    setCanScrollLeft(current > EPS);
    setCanScrollRight(current < maxScroll - EPS);
  };

  const scrollCarousel = (direction: 'left' | 'right') => {
    const el = carouselScrollRef.current;
    if (!el) return;

    // use responsive step so it feels right at all widths
    const step = Math.max(Math.floor(el.clientWidth * 0.8), 300);
    const maxScroll = Math.max(el.scrollWidth - el.clientWidth, 0);

    const target = direction === 'left' ? Math.max(0, el.scrollLeft - step) : Math.min(maxScroll, el.scrollLeft + step);

    el.scrollTo({ left: target, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = carouselScrollRef.current;
    if (!el) return;

    // Update on scroll
    el.addEventListener('scroll', updateArrows, { passive: true });

    // Update on resize
    const onResize = () => updateArrows();
    window.addEventListener('resize', onResize);

    // Update when content size changes
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);

    // Initial check
    updateArrows();

    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', onResize);
      ro.disconnect();
    };
  }, []);

  return (
    <motion.section ref={ref} className="relative bg-white py-8 text-xumoDarkPlum lg:py-32" data-testid={'text-section'}>
      <div className="absolute bottom-0 left-0 z-10 h-[500px] w-full" style={{ backgroundColor: '#EFF3F5' }} />

      <div className="wrapper flex flex-col">
        <StackedContent center content={content} className="text-xumoDarkPlum" />
      </div>

      <div className="pl-[50px] md:pl-[85px] lg:pl-[100px] 2xl:pl-[200px]">
        <motion.div
          ref={ref}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-30% 0% -30% 0%' }}
          className="type-subheading-light mt-20 flex items-center lg:mt-32"
        >
          <p data-i18n>Milestones</p>
        </motion.div>
        <div className="mb-6 mt-6 h-[1px] w-screen bg-xumoDarkPlum lg:mb-12" />
      </div>

      <div className="relative w-screen">
        <TimelineCarousel
          items={carouselItems}
          leadingGutterClass="w-[15px] md:w-[55px] lg:w-[70px] 2xl:w-[168px]"
          trailingGutterClass="w-[50px] md:w-20"
          scrollRef={carouselScrollRef}
        />
      </div>
      {/* Chevron scroll buttons (always visible; disabled at edges) */}
      <div className="absolute bottom-[30.5%] flex w-full justify-between">
        {/* Left */}
        <button
          type="button"
          onClick={() => scrollCarousel('left')}
          disabled={!canScrollLeft}
          aria-disabled={!canScrollLeft}
          className="z-20 flex h-16 w-10 items-center justify-center rounded-r-xl border-[1px] border-xumoDarkPlum bg-xumoDarkPlum text-white transition-all duration-300 hover:border-xumoDarkPlum disabled:border-[#EFF3F5] disabled:opacity-40 md:w-20 md:p-4"
          aria-label="Scroll left"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} className="h-8 w-8 stroke-current">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
        </button>
        {/* Right */}
        <button
          type="button"
          onClick={() => scrollCarousel('right')}
          disabled={!canScrollRight}
          aria-disabled={!canScrollRight}
          className="z-20 flex h-16 w-10 items-center justify-center rounded-l-xl border-[1px] border-xumoDarkPlum bg-xumoDarkPlum text-white transition-all duration-300 hover:border-xumoDarkPlum disabled:border-[#EFF3F5] disabled:opacity-40 md:w-20 md:p-4"
          aria-label="Scroll right"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} className="h-8 w-8 stroke-current">
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </button>
      </div>
    </motion.section>
  );
};

export default Timeline;
